/* eslint-disable i18next/no-literal-string */
import React from 'react';
import {useTranslation} from 'react-i18next';

import {IconAllInOne, IconDocument, IconEnvelope} from '@veneer/core';

import {PartnerGroup, TenantDetail} from '@br/idm-api';
import {Container, PageTitle, Paper, SUPPORT_EMAIL, Section} from '@br/mg-components';

interface PartnerEntity {
    partner: PartnerGroup;
    tenant?: undefined;
}
interface TenantEntity {
    partner?: undefined;
    tenant: TenantDetail;
}

const Support = ({partner, tenant}: PartnerEntity | TenantEntity): JSX.Element => {
    const {t} = useTranslation(['support']);
    const entity = partner || tenant;

    const controllerAttrs = document.querySelector('#data-attrs') as HTMLMetaElement;
    const idmAttrs = document.querySelector('[name=data-attrs]') as HTMLMetaElement;
    const version = (controllerAttrs || idmAttrs)?.dataset.version;
    const emailSubject = t('support:emailSubject', 'Wolf Pro Security request: {{name}}', {name: entity.name});
    const emailBody = `
${t('support:emailBody', 'Your message here')}

---

Diagnostic information:
ID: ${entity.id}
Name: ${entity.name}
Hostname: ${window.location.hostname}
Version: ${version || 'Unknown'}
Request time: ${new Date().toUTCString()}
`.trim();
    const supportEmailLink = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
        emailSubject
    )}&body=${encodeURIComponent(emailBody)}`;

    const showEmailSupportCard = partner !== undefined || tenant?.controller !== null;

    return (
        <Container>
            <Section>
                <PageTitle
                    centered
                    title={t('support:howCanWeHelp', 'How can we help?')}
                />
            </Section>
            <Section className="gap-8 d-flex flex-wrap">
                <Paper
                    className="col"
                    title={t('support:viewDocumentation', 'View documentation')}
                    icon={IconAllInOne}
                    primaryAction={{
                        children: t('support:visitSupportWebsite', 'Visit the support website'),
                        href: 'https://support.hpwolf.com/',
                        target: '__blank',
                    }}
                >
                    {t(
                        'support:accessDocumentation',
                        'Access online technical support documentation and see if your question is answered there.'
                    )}
                </Paper>
                {showEmailSupportCard && (
                    <Paper
                        className="col"
                        title={t('support:contactSupport', 'Contact support')}
                        icon={IconEnvelope}
                        primaryAction={{
                            children: t('support:sendAnEmail', 'Send an email'),
                            href: supportEmailLink,
                        }}
                    >
                        {t(
                            'support:contactOurTeam',
                            "Still can't find the answer to your question? Contact our support team via email."
                        )}
                    </Paper>
                )}
                <Paper
                    className="col"
                    title={t('support:legalDocuments', 'Legal documents')}
                    icon={IconDocument}
                    primaryAction={{
                        children: t('support:dataManagementFaq', 'Data Management FAQ'),
                        href: '/portal/faq/',
                        forceRedirect: true,
                    }}
                    secondaryAction={{
                        children: t('support:termsConditions', 'Terms and Conditions'),
                        href: '/portal/terms-and-conditions/',
                        forceRedirect: true,
                    }}
                >
                    {t(
                        'support:legalDocumentsDescription',
                        'Want to refer back to the legal documents? You can find links to them below.'
                    )}
                </Paper>
            </Section>
        </Container>
    );
};

export default Support;
